/* Écritures de la fiche de vente.
 *
 * Un principe tient tout le fichier : la commission est une conséquence, pas
 * une saisie. Chaque fois qu'une vente bouge — elle avance d'une étape, sa
 * facture usine arrive, son barème est repris à la main — on la réécrit, puis
 * on rejoue le moteur et on repose le résultat. Rien d'autre n'a le droit de
 * toucher aux montants de commission.
 *
 * Une commission facturée échappe à tout cela : elle est figée. */

import { supabase } from "@/lib/supabase";
import { detailCommission, etatApres, figee } from "@/domain/commissions";
import { blocage, etapeSuivante, porteCommission } from "@/domain/ventes";
import { SANS_EXCEDENT, excedentDe } from "@/domain/prix";
import type {
  BonCommande, CommVente, Commission, EtapeVente, Expedition, FactureUsine, Livraison,
  PartAgent, PieceJointe, RegleCommission, RegleSurplus, Vente,
} from "@/types/metier";

export interface ContexteCommission {
  regles: readonly RegleCommission[];
  parts: readonly PartAgent[];
  /** La commission déjà rattachée à la vente, s'il y en a une. */
  commission: Commission | null;
  /* L'excédent — ce que l'agent a vendu au-dessus de son plancher — ne se
     partage que si l'usine le permet. Sans règle ni plancher connu, il n'y a
     rien à partager et le calcul retombe sur l'ancien. */
  surplus?: readonly RegleSurplus[];
  planchers?: ReadonlyMap<string, number>;
}

function echoue(erreur: { message: string } | null): void {
  if (erreur) throw new Error(erreur.message);
}

/* ─────────────────────────────── pièces jointes ─────────────────────────── */

/** Un nom de fichier sûr pour le stockage, l'original restant affiché tel quel. */
function nomSur(nom: string): string {
  return nom
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .slice(-80);
}

/** Dépose la pièce dans le bac privé et rend de quoi la retrouver. */
export async function deposerPiece(venteId: string, fichier: File): Promise<PieceJointe> {
  const chemin = `${venteId}/${Date.now()}-${nomSur(fichier.name)}`;
  const { error } = await supabase.storage.from("pieces").upload(chemin, fichier);
  echoue(error);
  return { nom: fichier.name, taille: fichier.size, chemin };
}

/** Lien de consultation, valable une heure : le bac n'est pas public. */
export async function lienPiece(chemin: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from("pieces").createSignedUrl(chemin, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

/* ─────────────────────────────── commissions ────────────────────────────── */

/**
 * Numéro de la prochaine commission. La série est tenue côté base en lisant le
 * dernier numéro : à ce volume, deux créations simultanées sont improbables, et
 * la contrainte d'unicité ferait de toute façon échouer la seconde.
 */
async function prochaineReference(): Promise<string> {
  const { data, error } = await supabase
    .from("commissions")
    .select("reference")
    .order("reference", { ascending: false })
    .limit(1);
  echoue(error);
  const dernier = (data?.[0] as { reference?: string } | undefined)?.reference;
  const n = dernier ? Number(dernier.replace(/\D/g, "")) || 0 : 0;
  return `CO-${String(n + 1).padStart(4, "0")}`;
}

/**
 * Rejoue le moteur sur la vente et repose le résultat dans la commission.
 * Crée la commission si la vente vient d'atteindre le bon de commande.
 */
export async function synchroniserCommission(
  vente: Vente,
  ctx: ContexteCommission,
): Promise<void> {
  const existante = ctx.commission;
  if (!porteCommission(vente.etape)) return;
  if (existante && figee(existante)) return;

  const excedent =
    ctx.planchers && ctx.surplus?.length
      ? excedentDe(vente.lignes ?? [], vente.usine_id, ctx.planchers, ctx.surplus)
      : SANS_EXCEDENT;

  const d = detailCommission(vente, ctx.regles, ctx.parts, excedent);
  const champs = {
    vente_id: vente.id,
    agent_id: vente.agent_id,
    usine_id: vente.usine_id,
    etat: etatApres(existante?.etat ?? null, vente.etape),
    base: d.base,
    quantite: d.quantite,
    montant: d.montant,
    taux: d.taux,
    mode: d.mode,
    manuel: d.manuel,
    agent_montant: d.agentMontant,
    omnix_montant: d.omnixMontant,
    surplus: d.surplus,
    surplus_agent: d.surplusAgent,
    part_mode: d.partMode,
    part_valeur: d.partValeur,
  };

  if (existante) {
    const { error } = await supabase.from("commissions").update(champs).eq("id", existante.id);
    echoue(error);
    return;
  }
  const { error } = await supabase
    .from("commissions")
    .insert({ ...champs, reference: await prochaineReference() });
  echoue(error);
}

/* ─────────────────────────────── le devis ───────────────────────────────── */

export interface LigneSaisie {
  produit_id: string | null;
  ref: string;
  nom: string;
  gamme: string | null;
  conditionnement: string | null;
  /** Prix d'UNE unité. C'est lui qu'on négocie et que le plancher contrôle. */
  prix: number;
  /** Quantité en unités — ce que l'usine prépare, et ce que `total` multiplie. */
  qte: number;
  /** Les cartons saisis, d'où `qte` est tirée. */
  cartons: number;
  /** Ce que contenait un carton au moment du devis, figé avec la ligne. */
  par_carton: number | null;
}

/** Numéro du prochain devis, dans la même série que les ventes existantes. */
async function prochaineReferenceVente(): Promise<string> {
  const { data, error } = await supabase
    .from("ventes")
    .select("reference")
    .order("reference", { ascending: false })
    .limit(1);
  echoue(error);
  const dernier = (data?.[0] as { reference?: string } | undefined)?.reference;
  const n = dernier ? Number(dernier.replace(/\D/g, "")) || 0 : 0;
  return `SO-${String(n + 1).padStart(4, "0")}`;
}

/**
 * Crée un devis et ses lignes. Pas de commission à ce stade : elle naît au
 * passage en bon de commande.
 */
export async function creerDevis(entree: {
  client_id: string;
  usine_id: string;
  agent_id: string;
  contact: string | null;
  date_document: string;
  lignes: LigneSaisie[];
}): Promise<{ id: string; reference: string }> {
  if (!entree.lignes.length) throw new Error("Ajoutez au moins une ligne au devis.");

  const montant =
    Math.round(entree.lignes.reduce((s, l) => s + l.prix * l.qte, 0) * 100) / 100;
  const reference = await prochaineReferenceVente();

  const { data, error } = await supabase
    .from("ventes")
    .insert({
      reference,
      client_id: entree.client_id,
      usine_id: entree.usine_id,
      agent_id: entree.agent_id,
      contact: entree.contact,
      etape: "devis",
      date_document: entree.date_document,
      montant,
    })
    .select("id, reference")
    .single();
  echoue(error);
  const vente = data as { id: string; reference: string };

  const { error: erreurLignes } = await supabase.from("lignes_vente").insert(
    entree.lignes.map((l, i) => ({
      vente_id: vente.id,
      produit_id: l.produit_id,
      ref: l.ref,
      nom: l.nom,
      gamme: l.gamme,
      conditionnement: l.conditionnement,
      prix: l.prix,
      qte: l.qte,
      cartons: l.cartons,
      par_carton: l.par_carton,
      position: i,
    })),
  );
  if (erreurLignes) {
    /* Une vente sans ligne ne sert à rien : on la retire plutôt que de la
       laisser traîner à zéro. */
    await supabase.from("ventes").delete().eq("id", vente.id);
    throw new Error(erreurLignes.message);
  }
  return vente;
}

/* ──────────────────────────────── la vente ──────────────────────────────── */

async function ecrireVente(id: string, champs: Record<string, unknown>): Promise<void> {
  const { error } = await supabase.from("ventes").update(champs).eq("id", id);
  echoue(error);
}

/** Fait passer la vente à l'étape suivante, commission remise à jour derrière. */
export async function avancerVente(vente: Vente, ctx: ContexteCommission): Promise<EtapeVente> {
  const suivante = etapeSuivante(vente.etape);
  const empeche = blocage(vente);
  if (!suivante || empeche) throw new Error(empeche ?? "La vente est au terme de son parcours.");
  await ecrireVente(vente.id, { etape: suivante });
  await synchroniserCommission({ ...vente, etape: suivante }, ctx);
  return suivante;
}

/* Renseigner ce qu'une étape réclame. Chaque document se pose à part, puis
   la vente avance d'elle-même si rien ne l'en empêche — on ne demande pas
   deux gestes pour un seul fait. */
export async function poserBonCommande(
  vente: Vente,
  cote: "client" | "usine",
  bon: BonCommande,
  ctx: ContexteCommission,
): Promise<void> {
  const champ = cote === "client" ? "bdc_client" : "bdc_usine";
  const { error } = await supabase.from("ventes").update({ [champ]: bon }).eq("id", vente.id);
  echoue(error);
  await synchroniserCommission({ ...vente, [champ]: bon }, ctx);
}

export async function poserExpedition(
  vente: Vente, expedition: Expedition, ctx: ContexteCommission,
): Promise<void> {
  const { error } = await supabase.from("ventes").update({ expedition }).eq("id", vente.id);
  echoue(error);
  await synchroniserCommission({ ...vente, expedition }, ctx);
}

export async function poserLivraison(
  vente: Vente, livraison: Livraison, ctx: ContexteCommission,
): Promise<void> {
  const { error } = await supabase.from("ventes").update({ livraison }).eq("id", vente.id);
  echoue(error);
  await synchroniserCommission({ ...vente, livraison }, ctx);
}

export interface SaisieFacture {
  numero: string;
  date: string;
  montant: string;
  fichier: File | null;
}

/**
 * Rattache la facture de l'usine à la vente. C'est son montant, et non celui
 * du devis, qui sert ensuite de base à la commission.
 */
export async function enregistrerFactureUsine(
  vente: Vente,
  saisie: SaisieFacture,
  ctx: ContexteCommission,
): Promise<FactureUsine> {
  const numero = saisie.numero.trim();
  if (!numero) throw new Error("Saisissez le numéro de la facture usine.");

  const brut = parseFloat(String(saisie.montant).replace(",", "."));
  const facture: FactureUsine = {
    numero,
    date: saisie.date || vente.date_document,
    montant: Number.isFinite(brut) && brut > 0 ? brut : Number(vente.montant),
    fichier: saisie.fichier ? await deposerPiece(vente.id, saisie.fichier) : null,
  };

  await ecrireVente(vente.id, { facture_usine: facture });
  await synchroniserCommission({ ...vente, facture_usine: facture }, ctx);
  return facture;
}

/** Détache la facture pour en intégrer une autre. Le fichier déposé reste. */
export async function detacherFactureUsine(vente: Vente, ctx: ContexteCommission): Promise<void> {
  await ecrireVente(vente.id, { facture_usine: null });
  await synchroniserCommission({ ...vente, facture_usine: null }, ctx);
}

/**
 * Pose, modifie ou retire le barème propre à cette vente — comme on écrase un
 * prix. `null` la fait repasser sous le barème de l'usine.
 */
export async function reglerCommissionVente(
  vente: Vente,
  comm: CommVente | null,
  ctx: ContexteCommission,
): Promise<void> {
  const vide = !comm || (!comm.mode && !comm.partMode);
  const valeur = vide ? null : comm;
  await ecrireVente(vente.id, { comm: valeur });
  await synchroniserCommission({ ...vente, comm: valeur }, ctx);
}
